import { useCallback, useEffect, useState } from "react";
import { KycUserService } from "../../Services/KycUserService";

const useKycLogs = (user) => {
  const [kycLogs, setKycLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [dateRange, setDateRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  ]);

  const transform = (response = []) =>
    response.map((u, index) => ({
      applicant_id: u.applicant_id || `USER-${String(index + 1).padStart(4, "0")}`,
      user: u.name_enc || "Unknown User",
      contact: u.phone_enc || "N/A",
      email: u.email_enc || "N/A",
      datetime: new Date(u.createdAt || Date.now()).toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
      kyc_status: u.kyc_status || "Pending",
      rawData: u,
    }));

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await KycUserService.getUsers(user);
      const transformed = transform(response).sort((a, b) => {
        const toDate = (str) => {
          const [date, time] = str.split(", ");
          const [dd, mm, yyyy] = date.split("/");
          return new Date(`${yyyy}-${mm}-${dd} ${time}`);
        };
      
        return toDate(b.datetime) - toDate(a.datetime);
      });
      setKycLogs(transformed);
      setFilteredLogs(transformed);
    } catch (err) {
      console.error("useKycLogs: fetchUsers error", err);
      setError("Failed to fetch users data");
      setKycLogs([]);
      setFilteredLogs([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Filtering
  useEffect(() => {
    let filtered = [...kycLogs];

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter((log) => {
        const pan =
          (log.rawData?.verification_steps?.pan_verification?.data?.pan_result?.detectedDetails?.[0]?.panCardNumber || "").toLowerCase();
        return (
          (log.applicant_id || "").toLowerCase().includes(q) ||
          (log.user || "").toLowerCase().includes(q) ||
          (log.contact || "").toLowerCase().includes(q) ||
          pan.includes(q)
        );
      });
    }

    if (statusFilter && statusFilter !== "All Status") {
      filtered = filtered.filter((log) => {
        // check auditor_status and kyc_status fields
        const auditor = (log.rawData?.auditor_status || "").toLowerCase();
        const kyc = (log.kyc_status || "").toLowerCase();
        return auditor === statusFilter.toLowerCase() || kyc === statusFilter.toLowerCase();
      });
    }

    if (dateRange[0].startDate && dateRange[0].endDate) {
      filtered = filtered.filter((log) => {
        const logDate = new Date(
          log.rawData?.created_at ||
          log.rawData?.timestamps?.updated_at ||
          log.rawData?.createdAt ||
          Date.now()
        );
    
        return (
          logDate >= new Date(dateRange[0].startDate) &&
          logDate <= new Date(dateRange[0].endDate)
        );
      });
    }
    

    setFilteredLogs(filtered);
  }, [searchTerm, kycLogs, statusFilter, dateRange]);

  // initial fetch on mount
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleFiltersChange = (patch = {}) => {
    if (patch.searchTerm !== undefined) setSearchTerm(patch.searchTerm);
    if (patch.status !== undefined) setStatusFilter(patch.status);
    if (patch.dateRange !== undefined) setDateRange([patch.dateRange]);
  };

  return {
    kycLogs,
    filteredLogs,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    dateRange,
    setDateRange,
    fetchUsers,
    handleFiltersChange,
  };  
};

export default useKycLogs;
